import  { memo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faMapMarkerAlt,
  faEnvelope,
  faPhone,
  faPrint,
} from "@fortawesome/free-solid-svg-icons";
import {
  faYoutube,
  faLinkedin,
  faInstagram,
  faFacebook,
  faDiscord,
  faTelegram,
} from "@fortawesome/free-brands-svg-icons";

// Types
interface LinkItem {
  href: string;
  text: string;
}

interface SocialLink {
  href: string;
  icon: IconDefinition; // ✅ Correct type for FontAwesome icons
  label: string;
  color: string;
}

interface ContactItem {
  icon: IconDefinition; // ✅ Correct type for FontAwesome icons
  text: string;
}

// Data
const legalLinks: LinkItem[] = [
  { href: "/Private", text: "Privacy Policy" },
  { href: "/Terms", text: "Terms and Condition" },
  { href: "/Refund", text: "Refund & Cancellation Policy" },
];

const usefulLinks: LinkItem[] = [
  { href: "https://lms.forumde.in/courses", text: "Our Courses" },
  { href: "/achiver-wall", text: "Our Selection" },
  { href: "/", text: "Admin Login" },
  { href: "/blog", text: "Blog" },
  { href: "/certificate-validation", text: "Certificate Verification" },
];

const contactInfo: ContactItem[] = [
  { icon: faMapMarkerAlt, text: "B.No 958, Pocket C Gurugram, Haryana" },
  { icon: faEnvelope, text: "forumde127@gmail.com" },
  { icon: faPhone, text: "+91 7042928331" },
  { icon: faPrint, text: "+91 9801830173" },
];

const socialLinks: SocialLink[] = [
  { href: "https://www.youtube.com/@forum_de", icon: faYoutube, label: "YouTube", color: "hover:text-red-500" },
  { href: "https://www.linkedin.com/company/forumde/", icon: faLinkedin, label: "LinkedIn", color: "hover:text-blue-700" },
  { href: "https://www.instagram.com/forum_de_team", icon: faInstagram, label: "Instagram", color: "hover:text-pink-500" },
  { href: "https://www.facebook.com/Forum.DE.Team", icon: faFacebook, label: "Facebook", color: "hover:text-blue-600" },
  { href: "https://discord.gg/s2CjQAJVnZ", icon: faDiscord, label: "Discord", color: "hover:text-indigo-600" },
  { href: "https://t.me/+yvWJpw3nRrNhMmQ9", icon: faTelegram, label: "Telegram", color: "hover:text-blue-500" },
];

// Component
function FooterComponent(): JSX.Element {
  return (
    <footer className="bg-red-50 text-slate-800 w-full pt-12 pb-6 px-4 md:px-10">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        
        {/* Brand */}
        <div>
          <h2 className="text-3xl font-extrabold text-blue-800 mb-2">ForumDE</h2>
          <p className="text-sm text-slate-600 mb-4">Simplifying Technology For You.</p>

          <h3 className="text-md font-semibold mb-2">Follow Us</h3>
          <div className="flex gap-4 text-xl">
            {socialLinks.map((s, i) => (
              <a
                key={i}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className={`text-slate-500 transition hover:scale-110 ${s.color}`}
              >
                <FontAwesomeIcon icon={s.icon} />
              </a>
            ))}
          </div>
        </div>

        {/* Legal */}
        <div>
          <h3 className="text-lg font-bold text-blue-800 mb-3">Legal</h3>
          <ul className="space-y-2 text-sm">
            {legalLinks.map((link, idx) => (
              <li key={idx}>
                <a href={link.href} className="hover:underline hover:text-blue-600">
                  {link.text}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Useful Links */}
        <div>
          <h3 className="text-lg font-bold text-blue-800 mb-3">Useful Links</h3>
          <ul className="space-y-2 text-sm">
            {usefulLinks.map((link, idx) => (
              <li key={idx}>
                <a
                  href={link.href}
                  target={link.href.startsWith("http") ? "_blank" : "_self"}
                  rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="hover:underline hover:text-blue-600"
                >
                  {link.text}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h3 className="text-lg font-bold text-blue-800 mb-3">Contact</h3>
          <ul className="space-y-3 text-sm text-slate-700">
            {contactInfo.map((info, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <FontAwesomeIcon icon={info.icon} className="text-blue-500 w-4 mt-1" />
                <span>{info.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Footer Note */}
      <div className="mt-10 text-center text-xs text-slate-500 mb-6">
        © 2024{" "}
        <a href="https://forumde.in/" className="hover:underline font-semibold">
          ForumDE
        </a>{" "}
        — All Rights Reserved.
      </div>
    </footer>
  );
}

export const Footer = memo(FooterComponent);
